import React, { createContext, useContext, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "../lib/queryClient";
import LoadingTransition from "../components/ui/LoadingTransition";
import Skeleton from "../components/ui/Skeleton";

const AuthContext = createContext();

const fetchAuthStatus = async () => {
  const storedUser = localStorage.getItem("authUser");
  const storedToken = localStorage.getItem("authToken");

  if (!storedToken) throw new Error("Token missing");
  await apiRequest("/auth/check", {}, true);

  return {
    user: storedUser ? JSON.parse(storedUser) : null,
    token: storedToken,
  };
};

export function AuthProvider({ children }) {
  const getToken = () => localStorage.getItem("authToken");
  const {
    data,
    isLoading: queryLoading,
    error: queryError,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["auth-status", getToken()],
    queryFn: fetchAuthStatus,
    retry: false,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const [error, setError] = useState(null);
  const [loadingAction, setLoadingAction] = useState(false);
  const user = data?.user || null;
  const isAuthenticated = !!user;
  const isLoading = queryLoading || loadingAction;

  const login = async (email, password) => {
    setLoadingAction(true);
    setError(null);
    try {
      const res = await apiRequest(
        "/auth/admin",
        {
          method: "POST",
          body: JSON.stringify({ email, password }),
        },
        false
      );
      const { user, accessToken } = res.data || {};
      localStorage.setItem("authUser", JSON.stringify(user));
      localStorage.setItem("authToken", accessToken);
      await refetch();
      return { success: true };
    } catch (err) {
      const message = err.message || "Login failed.";
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoadingAction(false);
    }
  };

  const logout = async () => {
    localStorage.removeItem("authUser");
    localStorage.removeItem("authToken");
    await refetch();
  };

  const register = async (userData) => {
    setLoadingAction(true);
    setError(null);
    try {
      const data = await apiRequest(
        "/auth/register",
        {
          method: "POST",
          body: JSON.stringify(userData),
        },
        false
      );
      return { success: true, data };
    } catch (err) {
      setError(err.message || "Registration failed");
      return { success: false, error: err.message };
    } finally {
      setLoadingAction(false);
    }
  };

  const forgotPassword = async (email) => {
    setLoadingAction(true);
    setError(null);
    try {
      const data = await apiRequest(
        "/auth/forget-password",
        {
          method: "POST",
          body: JSON.stringify({ email }),
        },
        false
      );
      return data;
    } catch (err) {
      setError(err.message || "Forgot password failed");
      return { success: false, error: err.message };
    } finally {
      setLoadingAction(false);
    }
  };

  const resetPassword = async (body, token) => {
    setLoadingAction(true);
    setError(null);
    try {
      await apiRequest(
        "/auth/reset-password",
        {
          method: "POST",
          body: JSON.stringify({...body, "x-reset-token": token}),
        },
        false
      );
      return { success: true };
    } catch (err) {
      setError(err.message || "Reset password failed");
      return { success: false, error: err.message };
    } finally {
      setLoadingAction(false);
    }
  };

  const updateProfile = async (userData) => {
    setLoadingAction(true);
    setError(null);
    try {
      const data = await apiRequest("/auth/update-profile", {
        method: "PUT",
        body: JSON.stringify(userData),
      });
      localStorage.setItem("authUser", JSON.stringify(data));
      await refetch(); // reload new user
      return { success: true };
    } catch (err) {
      setError(err.message || "Update failed");
      return { success: false, error: err.message };
    } finally {
      setLoadingAction(false);
    }
  };

  const verifyOtp = async (body) => {
    setLoadingAction(true);
    setError(null);
    try {
      const data = await apiRequest(
        "/auth/verify-otp",
        {
          method: "POST",
          body: JSON.stringify(body),
        },
        false
      );
      return data;
    } catch (err) {
      setError(err.message || "OTP verification failed");
      return { success: false, error: err.message };
    } finally {
      setLoadingAction(false);
    }
  };

  const resendOtp = async (body) => {
    setLoadingAction(true);
    setError(null);
    try {
      const response = await apiRequest(
        "/auth/resend-otp",
        {
          method: "POST",
          body: JSON.stringify(body),
        },
        false
      );
      return response;
    } catch (err) {
      setError(err.message || "Resend OTP failed");
      return { success: false, error: err.message };
    } finally {
      setLoadingAction(false);
    }
  };

  

  const value = {
    user,
    isAuthenticated,
    isLoading,
    error: error || (isError ? queryError?.message : null),
    login,
    logout,
    register,
    resetPassword,
    updateProfile,
    getToken,
    forgotPassword,
    verifyOtp,
    resendOtp,
  };

  return (
    <AuthContext.Provider value={value}>
      <LoadingTransition isLoading={isLoading}>
        {isLoading ? <Skeleton /> : children}
      </LoadingTransition>
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
